/**
 * Hint Configuration for Dynamic Hint Type Labels and Icons
 *
 * The hint type registry maps hint type IDs to their display names, short labels,
 * and icon names. When selectedHintTypes is present in API data, labels come from
 * the registry. For legacy data without selectedHintTypes, falls back to fixed labels.
 */

import type { CategorySlug } from './categoryConfig';

// ─── Hint Type Registry (mirrors backend hintTypes.ts) ──────────────

export interface HintTypeConfig {
  displayName: string;
  shortLabel: string;
  icon: string; // lucide icon name
}

/**
 * Registry of all hint types with their display metadata.
 * Kept in sync with backend/src/config/hintTypes.ts.
 */
export const HINT_TYPE_REGISTRY: Record<string, HintTypeConfig> = {
  // Tier 1
  'legal-history':          { displayName: 'Legal History',              shortLabel: 'Legal',      icon: 'Scale' },
  'consumer-footprint':     { displayName: 'Consumer Footprint',         shortLabel: 'Consumer',   icon: 'ShoppingBag' },
  'celebrity-affiliation':  { displayName: 'Celebrity Affiliation',      shortLabel: 'Celebrity',  icon: 'Star' },
  'political-affiliation':  { displayName: 'Political Affiliation',      shortLabel: 'Political',  icon: 'Landmark' },
  // Tier 2
  'industry-position':      { displayName: 'Industry Position',          shortLabel: 'Industry',   icon: 'Factory' },
  'economic-sensitivity':   { displayName: 'Economic Sensitivity',       shortLabel: 'Economic',   icon: 'TrendingUp' },
  'competitive-advantage':  { displayName: 'Competitive Advantage',      shortLabel: 'Advantage',  icon: 'Shield' },
  'competitive-disadvantage': { displayName: 'Competitive Disadvantage', shortLabel: 'Weakness',   icon: 'AlertTriangle' },
  // Tier 3
  'founder':                { displayName: 'Founder',                    shortLabel: 'Founder',    icon: 'Crown' },
  'ceo':                    { displayName: 'CEO',                        shortLabel: 'CEO',        icon: 'Briefcase' },
  'super-investor':         { displayName: 'Super Investor',             shortLabel: 'Investor',   icon: 'Eye' },
  // Tier 4
  'product-service':        { displayName: 'Products & Services',        shortLabel: 'Products',   icon: 'Package' },
  'famous-brands':          { displayName: 'Famous Brands',              shortLabel: 'Brands',     icon: 'Award' },
};

// ─── Legacy Support ──────────────────────────────────────────────────

export type PromptSetType = 'nonIndustry' | 'industry';

export interface HintLabels {
  hint1: string;
  hint2: string;
  hint3: string;
  hint4: string;
}

const NON_INDUSTRY_LABELS: HintLabels = {
  hint1: 'Public Recognition',
  hint2: 'Industry Position',
  hint3: 'Founder & CEO',
  hint4: 'Products & Services'
};

const INDUSTRY_LABELS: HintLabels = {
  hint1: 'Public Recognition',
  hint2: 'Competitive (Dis)Advantage',
  hint3: 'Founder & CEO',
  hint4: 'Products & Services'
};

interface CategoryHintConfig {
  promptSet: PromptSetType;
  labels: HintLabels;
}

const CATEGORY_HINT_CONFIG: Record<CategorySlug, CategoryHintConfig> = {
  popular: { promptSet: 'nonIndustry', labels: NON_INDUSTRY_LABELS },
  wallstreetbets: { promptSet: 'nonIndustry', labels: NON_INDUSTRY_LABELS },
  foreign: { promptSet: 'nonIndustry', labels: NON_INDUSTRY_LABELS },
  tech: { promptSet: 'industry', labels: INDUSTRY_LABELS },
  pharma: { promptSet: 'industry', labels: INDUSTRY_LABELS },
  financial: { promptSet: 'industry', labels: INDUSTRY_LABELS },
  consumer: { promptSet: 'industry', labels: INDUSTRY_LABELS },
  energy: { promptSet: 'industry', labels: INDUSTRY_LABELS },
  industrial: { promptSet: 'industry', labels: INDUSTRY_LABELS }
};

/**
 * Get legacy hint labels for a given category.
 * Used as fallback when selectedHintTypes is not present.
 */
export function getHintLabels(category: string): HintLabels {
  const config = CATEGORY_HINT_CONFIG[category as CategorySlug];
  return config?.labels ?? NON_INDUSTRY_LABELS;
}

export function getPromptSetType(category: string): PromptSetType {
  const config = CATEGORY_HINT_CONFIG[category as CategorySlug];
  return config?.promptSet ?? 'nonIndustry';
}

// ─── Dynamic Label Resolution ────────────────────────────────────────

/**
 * Resolve display labels from selectedHintTypes array.
 * Falls back to legacy labels if a hint type ID is unknown.
 */
export function resolveHintTypeLabels(
  selectedHintTypes: [string, string, string, string]
): { displayName: string; shortLabel: string; icon: string }[] {
  return selectedHintTypes.map(id => {
    const config = HINT_TYPE_REGISTRY[id];
    if (config) return config;
    // Unknown type — use ID as fallback
    return {
      displayName: id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      shortLabel: id.split('-')[0],
      icon: 'Info',
    };
  });
}

/**
 * Get a shortened label for stepper display.
 * Works with both legacy full labels and dynamic hint type display names.
 */
export function getShortLabel(fullLabel: string): string {
  // Check hint type registry first (by displayName)
  for (const config of Object.values(HINT_TYPE_REGISTRY)) {
    if (config.displayName === fullLabel) return config.shortLabel;
  }
  // Legacy short labels
  const LEGACY_SHORT_LABELS: Record<string, string> = {
    'Public Recognition': 'Recognition',
    'Industry Position': 'Industry',
    'Competitive (Dis)Advantage': 'Competitive',
    'Founder & CEO': 'Founder',
    'Products & Services': 'Products',
  };
  return LEGACY_SHORT_LABELS[fullLabel] ?? fullLabel.split(' ')[0];
}
